/**
 * Shared area (/shared/...): bank-wide reference data with ONE owner each
 * (docs/architecture/admin-portal-shape.md §3). Retail owns all three today (Bill 2026-10-16):
 * the holiday calendar, branches and regions. Other platforms read them through pcb-retail-api
 * and never keep their own copy.
 */

import { NavLink, Outlet } from 'react-router';
import { PageHeader } from '@retail-ops/panel-shell';
import { useAuth } from '@/contexts/AuthContext';
import { usePlatforms } from '@/contexts/PlatformsContext';

const ITEMS: ReadonlyArray<{ slug: string; label: string; roles: ReadonlyArray<string> | null }> = [
  { slug: 'holidays', label: 'Holiday calendar', roles: null },
  { slug: 'branches', label: 'Branches', roles: ['SuperAdmin', 'Admin', 'RetailAdmin'] },
  { slug: 'regions', label: 'Regions', roles: ['SuperAdmin', 'Admin', 'RetailAdmin'] },
];

export function sharedItemsFor(roles: ReadonlyArray<string>): typeof ITEMS {
  return ITEMS.filter((it) => !it.roles || it.roles.some((r) => roles.includes(r)));
}

export function SharedArea(): JSX.Element {
  const { user } = useAuth();
  const { status } = usePlatforms();
  if (status.retail.kind === 'loading') return <div className="loading-inline">Checking your admin rights…</div>;
  if (status.retail.kind !== 'ready') {
    return <div className="empty">Shared reference data is kept by Retail Operations; you have no admin rights there.</div>;
  }
  const items = sharedItemsFor(user?.roles ?? []);
  return (
    <div className="domain-shell">
      <PageHeader title="Shared reference data" intro="Kept by Retail Operations, read by every platform." />
      <nav className="domain-shell__subnav" aria-label="Shared lists">
        {items.map((it) => (
          <NavLink key={it.slug} to={`/shared/${it.slug}`}>
            {it.label}
          </NavLink>
        ))}
      </nav>
      <div className="platform__body">
        <Outlet />
      </div>
    </div>
  );
}
